/**
 * When a block was said — the clock Clean View never had.
 *
 * owner, 2026-08-20: 有辦法再 Clean View 裡顯示對話 Block 傳遞的時間點嗎? 這個
 * Feature 感覺會很方便.
 *
 * It is more than convenient. The morning this was asked, Clean View had spent
 * hours showing a conversation that stopped growing at 05:47 — the pin pointed at
 * a session the agent had moved on from — and the only way anyone could tell was
 * that the content "felt old". A transcript with no clock cannot say "nothing has
 * happened here since breakfast"; it just keeps showing the last thing it knows,
 * indistinguishable from a quiet agent. The last block's time says it out loud.
 *
 * `ts` is epoch SECONDS (avg-main's wire contract, e99797c-era): the moment the
 * record was WRITTEN — the model saying it, the tool returning — never the moment
 * the browser received it, which backfill would collapse to a single meaningless
 * instant.
 *
 * OPTIONAL, AND ABSENT IS A REAL ANSWER. Two of the five transcript paths cannot
 * supply it: Antigravity's protobuf fallback carries no per-record time, and
 * cursor-agent only stamps the USER half (owner ruled the scope: "Cursor 只有 user
 * 那半, 那就做 user 的發話時間就好"). Nothing here invents one. Interpolating a
 * plausible time between two known ones would make a stalled transcript look
 * alive, which is the exact disease this feature exists to diagnose.
 */

/** Anything carrying the wire's optional stamp. */
export interface Stamped {
  ts?: number
}

const MINUTE = 60
/** Below this a folded group is one burst, and a range would be noise, not information. */
const SPAN_THRESHOLD = 5 * MINUTE

const two = (n: number) => String(n).padStart(2, '0')

/**
 * The newest stamp in a group, or null when the group has none.
 *
 * NOT the first: a folded row answers "how recent is this", and only the last
 * block can answer it. A group where some blocks are stamped and some are not
 * (a claude turn whose tool results came from a source without a clock) reports
 * the newest one it actually has, never a gap.
 */
export function lastTs(blocks: readonly Stamped[]): number | null {
  let out: number | null = null
  for (const b of blocks) {
    if (typeof b.ts === 'number' && Number.isFinite(b.ts) && (out === null || b.ts > out)) out = b.ts
  }
  return out
}

/** The oldest stamp in a group, or null — the opening edge of a span. */
export function firstTs(blocks: readonly Stamped[]): number | null {
  let out: number | null = null
  for (const b of blocks) {
    if (typeof b.ts === 'number' && Number.isFinite(b.ts) && (out === null || b.ts < out)) out = b.ts
  }
  return out
}

const sameLocalDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

/**
 * One stamp, rendered for a row.
 *
 * Today is `HH:MM`; any other day carries `MM-DD` in front, because "09:12" on a
 * transcript scrolled back three days reads as this morning and is off by three
 * days. Seconds never appear here — at row width they are four characters of
 * precision nobody is reading; they live in the tooltip, where someone correlating
 * against a log actually wants them.
 */
export function clockLabel(ts: number, now: Date = new Date()): string {
  const d = new Date(ts * 1000)
  const hhmm = `${two(d.getHours())}:${two(d.getMinutes())}`
  return sameLocalDay(d, now) ? hhmm : `${two(d.getMonth() + 1)}-${two(d.getDate())} ${hhmm}`
}

/**
 * The row's visible time: a single clock, or a range when the group really spans one.
 *
 * A folded turn is usually a burst — six tool calls inside four seconds — and
 * printing "14:20–14:20" there spends double the width to say the same thing while
 * implying a duration that is not real. So the range appears only past five
 * minutes, where it carries information the single stamp cannot: this agent was
 * working on that for half an hour.
 *
 * Returns null when the group has no stamp at all. The caller must render NOTHING
 * for null — not a dash, not "—". A placeholder makes a source that has no clock
 * look like a clock that is broken, and readers debug the wrong thing.
 */
export function timeLabel(blocks: readonly Stamped[], now: Date = new Date()): string | null {
  const last = lastTs(blocks)
  if (last === null) return null
  const first = firstTs(blocks)
  if (first !== null && last - first > SPAN_THRESHOLD) {
    return `${clockLabel(first, now)}–${clockLabel(last, now)}`
  }
  return clockLabel(last, now)
}

/** Full precision for the tooltip — where someone lining this up against a log looks. */
export function timeTitle(blocks: readonly Stamped[]): string | undefined {
  const last = lastTs(blocks)
  if (last === null) return undefined
  const first = firstTs(blocks)
  const iso = (t: number) => new Date(t * 1000).toISOString().replace('.000', '')
  return first !== null && first !== last ? `${iso(first)} → ${iso(last)}` : iso(last)
}

